/**
 * RegistrationCompletePage Component
 * 라이더 회원가입 완료 페이지 (비밀번호, 휴대폰번호, 이메일 입력)
 * VROONG 브랜드 통일 디자인
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRegistrationVerify, useRegistrationComplete } from '../hooks/useRegistration';
import { getCookie } from '../../../core/utils/cookie';
import styles from '../styles/RegistrationPage.module.css';

const RegistrationCompletePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const emailFromUrl = searchParams.get('email');

    const { handleVerifyToken, isLoading: isVerifying, error: verifyError, riderInfo } = useRegistrationVerify();
    const { handleComplete, isLoading: isCompleting, error: completeError, setError } = useRegistrationComplete();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [phoneNum, setPhoneNum] = useState('');
    const [email, setEmail] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // 로그인 상태 확인
    useEffect(() => {
        const accessToken = getCookie('accessToken');
        if (accessToken) {
            setIsLoggedIn(true);
        }
    }, []);

    // 토큰 검증
    useEffect(() => {
        if (token && !isLoggedIn) {
            handleVerifyToken(token);
        }
    }, [token, handleVerifyToken, isLoggedIn]);

    // 이메일 초기값 설정 (URL 파라미터 또는 백엔드 응답에서)
    useEffect(() => {
        if (emailFromUrl) {
            setEmail(decodeURIComponent(emailFromUrl));
        } else if (riderInfo?.email) {
            setEmail(riderInfo.email);
        }
    }, [emailFromUrl, riderInfo]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // 비밀번호 확인
        if (password !== confirmPassword) {
            setError('비밀번호가 일치하지 않습니다');
            return;
        }

        // 비밀번호 형식 검증 (숫자, 영문, 특수문자 포함 8자 이상)
        const passwordRegex = /^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[!@#$%^&*(),.?":{}|<>[\]\\/_.+-]).{8,}$/;
        if (!passwordRegex.test(password)) {
            setError('비밀번호는 숫자, 영문, 특수문자를 포함하여 8자리 이상이어야 합니다');
            return;
        }

        // 기존 사용자가 아닌 경우에만 휴대폰번호 검증
        if (!riderInfo?.existingUser) {
            // 휴대폰번호 형식 검증
            const phoneRegex = /^01[0-9]{8,9}$/;
            if (!phoneRegex.test(phoneNum.replace(/-/g, ''))) {
                setError('유효한 휴대폰번호 형식이 아닙니다 (예: 01012345678)');
                return;
            }

            // 이메일 형식 검증
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                setError('유효한 이메일 형식이 아닙니다');
                return;
            }
        }

        if (token) {
            await handleComplete(
                token,
                password,
                confirmPassword,
                phoneNum.replace(/-/g, ''),
                email
            );
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const handleGoToHome = () => {
        navigate('/home');
    };

    // 이미 로그인된 사용자
    if (isLoggedIn) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>회원가입</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']} style={{ backgroundColor: '#3b82f6' }}>
                            <i className="fas fa-user-check" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']} style={{ color: '#3b82f6' }}>이미 로그인 되어있습니다</h2>
                        <p className={styles['status-description']}>
                            현재 로그인된 상태입니다.<br />
                            새로운 계정을 만들려면 먼저 로그아웃해주세요.
                        </p>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={handleGoToHome}
                        >
                            홈으로 이동
                        </button>
                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 토큰이 없는 경우
    if (!token) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>회원가입</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                            <i className="fas fa-times" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']} style={{ color: '#ef4444' }}>유효하지 않은 접근</h2>
                        <p className={styles['status-description']}>
                            회원가입 인증이 필요합니다.<br />
                            회원가입 페이지에서 다시 시도해주세요.
                        </p>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={() => navigate('/registration-request')}
                        >
                            회원가입 페이지로
                        </button>
                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 토큰 검증 중
    if (isVerifying) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>회원가입</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']} style={{ textAlign: 'center', padding: '60px 24px' }}>
                        <div className={styles['btn-spinner']} style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 20px',
                            borderWidth: '3px'
                        }}></div>
                        <p style={{ color: '#6b7280', fontSize: '15px' }}>토큰 검증 중...</p>
                    </div>
                </div>
            </div>
        );
    }

    // 토큰 검증 실패
    if (verifyError) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>회원가입</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
                            <i className="fas fa-times" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']} style={{ color: '#ef4444' }}>토큰 검증 실패</h2>
                        <p className={styles['status-description']}>
                            {verifyError}
                        </p>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={() => navigate('/registration-request')}
                        >
                            회원가입 다시 시도
                        </button>
                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 토큰 검증 성공, 회원가입 폼 표시
    return (
        <div className={styles['registration-screen']}>
            {/* 헤더 */}
            <div className={styles['registration-header']}>
                <div className={styles['header-content']}>
                    <button className={styles['back-btn']} onClick={() => navigate('/registration-request')}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className={styles['header-title']}>
                        {riderInfo?.existingUser ? '비밀번호 변경' : '회원가입 완료'}
                    </h1>
                    <div className={styles['header-spacer']}></div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['registration-container']}>
                <div className={styles['card']}>
                    <div className={styles['card-header']}>
                        <div className={styles['card-icon']}>
                            <i className={riderInfo?.existingUser ? 'fas fa-key' : 'fas fa-user-check'}></i>
                        </div>
                        <h2 className={styles['card-title']}>
                            {riderInfo?.existingUser ? '비밀번호 변경' : '회원가입 완료'}
                        </h2>
                        <p className={styles['card-description']}>
                            {riderInfo?.existingUser
                                ? '새로운 비밀번호를 설정해주세요.'
                                : '정보를 입력하여 회원가입을 완료해주세요.'
                            }
                        </p>
                    </div>

                    {/* 라이더 정보 표시 */}
                    <div className={styles['rider-info-card']}>
                        <div className={styles['rider-info-header']}>
                            <i className="fas fa-user-check"></i>
                            <span>라이더 정보</span>
                        </div>
                        <div className={styles['rider-info-content']}>
                            <div className={styles['rider-info-row']}>
                                <span className={styles['rider-info-label']}>협력사 ID</span>
                                <span className={styles['rider-info-value']}>{riderInfo?.suppId || '-'}</span>
                            </div>
                            <div className={styles['rider-info-row']}>
                                <span className={styles['rider-info-label']}>라이더명</span>
                                <span className={styles['rider-info-value']}>{riderInfo?.riderName || '-'}</span>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className={styles['form-section']}>
                        {/* 비밀번호 입력 */}
                        <div className={styles['form-field']}>
                            <label className={styles['field-label']}>
                                비밀번호 <span className={styles['required']}>*</span>
                            </label>
                            <div className={styles['input-wrapper']}>
                                <i className={`fas fa-lock ${styles['input-icon']}`}></i>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className={styles['input-field']}
                                    placeholder="비밀번호를 입력해주세요"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={isCompleting}
                                />
                                <button
                                    type="button"
                                    className={styles['password-toggle']}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                            <span className={styles['helper-text']}>숫자, 영문, 특수문자 포함 8자리 이상</span>
                        </div>

                        {/* 비밀번호 확인 */}
                        <div className={styles['form-field']}>
                            <label className={styles['field-label']}>
                                비밀번호 확인 <span className={styles['required']}>*</span>
                            </label>
                            <div className={styles['input-wrapper']}>
                                <i className={`fas fa-lock ${styles['input-icon']}`}></i>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className={styles['input-field']}
                                    placeholder="비밀번호를 다시 입력해주세요"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={isCompleting}
                                />
                                <button
                                    type="button"
                                    className={styles['password-toggle']}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        {/* 신규 사용자만 휴대폰번호와 이메일 입력 */}
                        {!riderInfo?.existingUser && (
                            <>
                                {/* 휴대폰번호 */}
                                <div className={styles['form-field']}>
                                    <label className={styles['field-label']}>
                                        휴대폰번호 <span className={styles['required']}>*</span>
                                    </label>
                                    <div className={styles['input-wrapper']}>
                                        <i className={`fas fa-phone ${styles['input-icon']}`}></i>
                                        <input
                                            type="tel"
                                            className={styles['input-field']}
                                            placeholder="휴대폰번호를 입력해주세요"
                                            value={phoneNum}
                                            onChange={(e) => setPhoneNum(e.target.value.replace(/[^0-9]/g, ''))}
                                            required
                                            disabled={isCompleting}
                                            maxLength={11}
                                        />
                                    </div>
                                    <span className={styles['helper-text']}>하이픈(-) 없이 숫자만 입력</span>
                                </div>

                                {/* 이메일 */}
                                <div className={styles['form-field']}>
                                    <label className={styles['field-label']}>
                                        이메일 <span className={styles['required']}>*</span>
                                    </label>
                                    <div className={styles['input-wrapper']}>
                                        <i className={`fas fa-envelope ${styles['input-icon']}`}></i>
                                        <input
                                            type="email"
                                            className={styles['input-field']}
                                            placeholder="이메일을 입력해주세요"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            disabled={isCompleting}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {completeError && (
                            <div className={styles['error-message']}>
                                <i className="fas fa-exclamation-circle"></i>
                                {completeError}
                            </div>
                        )}

                        <button type="submit" className={styles['btn-primary']} disabled={isCompleting}>
                            {isCompleting ? (
                                <>
                                    <span className={styles['btn-spinner']}></span>
                                    처리 중...
                                </>
                            ) : (riderInfo?.existingUser ? '비밀번호 변경' : '회원가입 완료')}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegistrationCompletePage;
